import styled, { css, RuleSet } from 'styled-components'

import { MarketShopOrderStatus } from '@/models'

export const OrdersColumn = styled.section<{
  $status: MarketShopOrderStatus
  $isCollapsed?: boolean
  $isHighlighted?: boolean
  $isDimmed?: boolean
}>(({ theme, $status, $isCollapsed, $isHighlighted, $isDimmed }) => {
  const baseStyles = css`
    // NOTE: flex-basis is based on column width (4rem) + horizontal padding of the column from both sides (theme.space['8'] * 2)
    flex: ${$isCollapsed ? 0 : 1} ${$isCollapsed ? 1 : 0} calc(4rem + ${theme.space['8']} * 2);
    display: flex;
    flex-direction: column;
    gap: ${theme.space['16']};
    padding: calc(${theme.space['8']} - ${theme.border.size['1']});
    border-radius: ${theme.border.radius['16']};
    border: ${theme.border.size['1']} solid;
    overflow: hidden;
    opacity: ${$isDimmed ? 0.5 : 1};
    transition:
      flex ${theme.transition.duration['md']} linear,
      opacity ${theme.transition.duration['md']} ease-in-out,
      background-color ${theme.transition.duration['md']} ease-in-out,
      border-color ${theme.transition.duration['md']} ease-in-out;
    container-type: inline-size;
  `

  const typeStyles: Record<MarketShopOrderStatus, RuleSet> = {
    awaiting_pickup: css`
      background-color: ${theme.colors.green100};
      border-color: ${theme.colors.green200};

      ${
        $isHighlighted &&
        css`
        border-color: ${theme.colors.green500};
      `
      }
    `,
    completed: css`
      background-color: ${theme.colors.gray100};
      border-color: ${theme.colors.gray400};

      ${
        $isHighlighted &&
        css`
        border-color: ${theme.colors.gray800};
      `
      }
    `,
    declined: css`
      background-color: ${theme.colors.red100};
      border-color: ${theme.colors.red200};

      ${
        $isHighlighted &&
        css`
        border-color: ${theme.colors.red500};
      `
      }
    `,
    in_progress: css`
      background-color: ${theme.colors.blue100};
      border-color: ${theme.colors.blue200};

      ${
        $isHighlighted &&
        css`
        border-color: ${theme.colors.blue500};
      `
      }
    `,
    new: css`
      background-color: ${theme.colors.orange100};
      border-color: ${theme.colors.orange200};

      ${
        $isHighlighted &&
        css`
        border-color: ${theme.colors.orange500};
      `
      }
    `,
  }

  return css`
    ${baseStyles}
    ${typeStyles[$status]}
  `
})

export const Header = styled.header<{ $status: MarketShopOrderStatus; $isCollapsed?: boolean }>(
  ({ theme, $status, $isCollapsed }) => {
    const baseStyles = css`
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: ${theme.space['8']};
      color: ${theme.colors.black800};
      min-width: fit-content;
      container: hide-name / inline-size;

      & > svg:first-child {
        flex-shrink: 0;
        padding: ${theme.space['8']};
      }

      & > *:nth-child(2) {
        flex: ${$isCollapsed ? '1 1 0' : '1 1 auto'};
        container-type: inline-size;
        transition: flex ${theme.transition.duration['md']} linear;
      }

      // NOTE: 12rem is width of visible columns + gaps between them * on columns amount
      @container hide-name (max-width: calc(12rem + ${theme.space['8']} * 3)) {
        & > *:nth-child(2) {
          display: none;
        }
      }
    `

    const typeStyles: Record<MarketShopOrderStatus, RuleSet> = {
      awaiting_pickup: css`
        & > svg:first-child {
          color: ${theme.colors.green500};
        }
      `,
      completed: css`
        & > svg:first-child {
          color: ${theme.colors.black500};
        }
      `,
      declined: css`
        & > svg:first-child {
          color: ${theme.colors.red500};
        }
      `,
      in_progress: css`
        & > svg:first-child {
          color: ${theme.colors.blue500};
        }
      `,
      new: css`
        & > svg:first-child {
          color: ${theme.colors.orange500};
        }
      `,
    }

    return css`
      ${baseStyles}
      ${typeStyles[$status]}
    `
  },
)

export const Count = styled.div<{ $status: MarketShopOrderStatus }>(({ theme, $status }) => {
  const baseStyles = css`
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 4rem;
    height: 4rem;
    border-radius: ${theme.border.radius['12']};
  `

  const typeStyles: Record<MarketShopOrderStatus, RuleSet> = {
    awaiting_pickup: css`
      background-color: ${theme.colors.green200};
      color: ${theme.colors.green500};
    `,
    completed: css`
      background-color: ${theme.colors.gray300};
      color: ${theme.colors.black500};
    `,
    declined: css`
      background-color: ${theme.colors.red200};
      color: ${theme.colors.red500};
    `,
    in_progress: css`
      background-color: ${theme.colors.blue200};
      color: ${theme.colors.blue500};
    `,
    new: css`
      background-color: ${theme.colors.orange200};
      color: ${theme.colors.orange500};
    `,
  }

  return css`
    ${baseStyles}
    ${typeStyles[$status]}
  `
})

export const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space['12']};
    // NOTE: Content should have same border radius as 'OrderCard', so during scroll it looks seamlessly
    border-radius: ${theme.border.radius['12']};
    overflow: hidden auto;

    &::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  `,
)
